import { createSlice } from "@reduxjs/toolkit";

import {
  addJobsTypeAction,
  addJobsAction,
  addRecurringJobsDetailsAction,
  changeJobsTypeStatusAction,
  getJobsTypeListAction,
  getSingleJobsTypeAction,
  getRecurringJobsDetailsAction,
  updateJobsTypeAction,
  getJobsListAction,
  getSingleJobsAction,
  updateJobsAction,
  getRecurringResponsiblePeopleAction,
  addJobLinkAction,
  addJobCommentsAction,
  getJobCommentsDetailsAction,
  getJobLinksDetailsAction,
  getUserMapedClientJobListAction,
  checkUniqueJobTypeAction,
  shareJobsWithTeamMembersAction,
  getUserMapedClientShareJobListAction,
  getFutureJobsListAction,
  getJobChecklistDataAction,
  getJobChecklistSingleDataAction,
  addJobChecklistDataAction,
  updateJobChecklistDataAction,
  addJobClosureAction,
  getAllOpenJobChecklistSingleDataAction,
  getAllOpenJobChecklistAction,
  updateAllOpenJobChecklistAction,
  lockJobChecklistDataAction,
  getCommentDueTomorrowJobListAction,
  getRecurringJobReportAction,
  initialState,
  jobsReducer,
} from "@/redux/jobs";
import type { JobsTypes } from "@/redux/jobs/jobsType";

export const jobsSlice = createSlice({
  name: "jobs",
  initialState,
  reducers: jobsReducer,
  extraReducers: (builder) => {
    builder
      .addCase(getJobsTypeListAction.pending, (state: JobsTypes) => ({
        ...state,
        jobTypeFetching: true,
      }))
      .addCase(
        getJobsTypeListAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const {
            result = {},
            message,
            status_code,
            total_count,
            success,
          } = payload;

          if (success === 0) {
            return {
              ...state,
              jobTypeFetching: false,
              error: message,
              success: 0,
              statusCode: status_code,
            };
          }
          return {
            ...state,
            jobTypeFetching: false,
            jobsTypeList: result,
            jobsTypeListTotalCount: total_count,
          };
        }
      )
      .addCase(
        getJobsTypeListAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          jobTypeFetching: false,
          error: error?.message,
          success: 0,
        })
      )

      .addCase(addJobsTypeAction.pending, (state: JobsTypes) => ({
        ...state,
        jobTypeFetching: true,
      }))
      .addCase(addJobsTypeAction.fulfilled, (state: JobsTypes, { payload }) => {
        const { success, message, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            jobTypeFetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }
        return {
          ...state,
          jobTypeFetching: false,
          message,
          success,
        };
      })
      .addCase(addJobsTypeAction.rejected, (state: JobsTypes, { error }) => ({
        ...state,
        jobTypeFetching: false,
        error: error?.message,
        success: 0,
      }))

      .addCase(updateJobsTypeAction.pending, (state: JobsTypes) => ({
        ...state,
        jobTypeFetching: true,
      }))
      .addCase(
        updateJobsTypeAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { success, message, status_code } = payload;
          if (success === 0) {
            return {
              ...state,
              jobTypeFetching: false,
              error: message,
              statusCode: status_code,
              success,
            };
          }
          return {
            ...state,
            jobTypeFetching: false,
            message,
            success,
          };
        }
      )
      .addCase(
        updateJobsTypeAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          jobTypeFetching: false,
          error: error?.message,
          success: 0,
        })
      )

      .addCase(getSingleJobsTypeAction.pending, (state: JobsTypes) => ({
        ...state,
        jobTypeFetching: true,
      }))
      .addCase(
        getSingleJobsTypeAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { result = {}, message, status_code, success } = payload;

          if (success === 0) {
            return {
              ...state,
              jobTypeFetching: false,
              error: message,
              success: 0,
              statusCode: status_code,
            };
          }
          return {
            ...state,
            jobTypeFetching: false,
            singleJobsType: result,
          };
        }
      )
      .addCase(
        getSingleJobsTypeAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          fetching: false,
          error: error?.message,
          success: 0,
        })
      )

      .addCase(getRecurringJobsDetailsAction.pending, (state: JobsTypes) => ({
        ...state,
        fetching: true,
      }))
      .addCase(
        getRecurringJobsDetailsAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { result, message, status_code, success } = payload;
          if (success === 0) {
            return {
              ...state,
              fetching: false,
              error: message,
              success: 0,
              statusCode: status_code,
            };
          }

          return {
            ...state,
            fetching: false,
            singleRecurringJobsDetails: result[0],
            totalRecurringJobsListCount: result[0]?.total_count,
          };
        }
      )
      .addCase(
        getRecurringJobsDetailsAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          fetching: false,
          error: error?.message,
          success: 0,
        })
      )

      .addCase(addRecurringJobsDetailsAction.pending, (state: JobsTypes) => ({
        ...state,
        fetching: true,
      }))
      .addCase(
        addRecurringJobsDetailsAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { message, status_code, success } = payload;

          if (success === 0) {
            return {
              ...state,
              fetching: false,
              error: message,
              success: 0,
              statusCode: status_code,
            };
          }

          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            message,
          };
        }
      )
      .addCase(
        addRecurringJobsDetailsAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          fetching: false,
          error: error?.message,
          success: 0,
        })
      )

      .addCase(addJobsAction.pending, (state: JobsTypes) => ({
        ...state,
        fetching: true,
      }))
      .addCase(addJobsAction.fulfilled, (state: JobsTypes, { payload }) => {
        const { success, message, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }
        return {
          ...state,
          fetching: false,
          message,
          success,
        };
      })
      .addCase(addJobsAction.rejected, (state: JobsTypes, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message,
        success: 0,
      }))
      .addCase(changeJobsTypeStatusAction.pending, (state: JobsTypes) => ({
        ...state,
        fetching: true,
      }))
      .addCase(
        changeJobsTypeStatusAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { success, message, status_code } = payload;
          if (success === 0) {
            return {
              ...state,
              fetching: false,
              error: message,
              statusCode: status_code,
              success,
            };
          }
          return {
            ...state,
            fetching: false,
            message,
            success,
          };
        }
      )
      .addCase(
        changeJobsTypeStatusAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          fetching: false,
          error: error?.message,
          success: 0,
        })
      )
      .addCase(getJobsListAction.pending, (state: JobsTypes) => ({
        ...state,
        fetching: true,
      }))
      .addCase(getJobsListAction.fulfilled, (state: JobsTypes, { payload }) => {
        const { success, message, status_code, result, total_count } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }
        return {
          ...state,
          fetching: false,
          message,
          success,
          jobsList: result,
          totalJobsCount: total_count,
        };
      })
      .addCase(getJobsListAction.rejected, (state: JobsTypes, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message,
        success: 0,
      }))
      .addCase(getFutureJobsListAction.pending, (state: JobsTypes) => ({
        ...state,
        fetching: true,
      }))
      .addCase(
        getFutureJobsListAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { success, message, status_code, result, total_count } =
            payload;
          if (success === 0) {
            return {
              ...state,
              fetching: false,
              error: message,
              statusCode: status_code,
              success,
            };
          }
          return {
            ...state,
            fetching: false,
            message,
            success,
            futureJobsList: result,
          };
        }
      )
      .addCase(
        getFutureJobsListAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          fetching: false,
          error: error?.message,
          success: 0,
        })
      )
      .addCase(getSingleJobsAction.pending, (state: JobsTypes) => ({
        ...state,
        fetching: true,
      }))
      .addCase(
        getSingleJobsAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { result = {}, message, status_code, success } = payload;

          if (success === 0) {
            return {
              ...state,
              fetching: false,
              error: message,
              success: 0,
              statusCode: status_code,
            };
          }
          return {
            ...state,
            fetching: false,
            singleJobs: result,
          };
        }
      )
      .addCase(getSingleJobsAction.rejected, (state: JobsTypes, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message,
        success: 0,
      }))

      .addCase(updateJobsAction.pending, (state: JobsTypes) => ({
        ...state,
        fetching: true,
      }))
      .addCase(updateJobsAction.fulfilled, (state: JobsTypes, { payload }) => {
        const { success, message, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            statusCode: status_code,
            success,
          };
        }
        return {
          ...state,
          fetching: false,
          message,
          success,
        };
      })
      .addCase(updateJobsAction.rejected, (state: JobsTypes, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message,
        success: 0,
      }))

      .addCase(
        getRecurringResponsiblePeopleAction.pending,
        (state: JobsTypes) => ({
          ...state,
          fetching: true,
        })
      )
      .addCase(
        getRecurringResponsiblePeopleAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { result = {}, message, status_code, success } = payload;

          if (success === 0) {
            return {
              ...state,
              fetching: false,
              error: message,
              success: 0,
              statusCode: status_code,
            };
          }
          return {
            ...state,
            fetching: false,
            recurringResponsiblePeople: result,
          };
        }
      )
      .addCase(
        getRecurringResponsiblePeopleAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          fetching: false,
          error: error?.message,
          success: 0,
        })
      )
      .addCase(getJobCommentsDetailsAction.pending, (state: JobsTypes) => ({
        ...state,
        jobProgressCommentsFetching: true,
      }))
      .addCase(
        getJobCommentsDetailsAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { result = {}, message, status_code, success } = payload;
          if (success === 0) {
            return {
              ...state,
              error: message,
              jobProgressCommentsFetching: false,
              jobProgressCommentsSuccess: success,
              statusCode: status_code,
            };
          }
          return {
            ...state,
            jobProgressCommentsFetching: false,
            jobProgressCommentsList: result,
            statusCode: status_code,
            // jobProgressCommentsSuccess: success,
          };
        }
      )
      .addCase(
        getJobCommentsDetailsAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          error: error?.message,
          jobProgressCommentsFetching: false,
          jobProgressCommentsSuccess: 0,
        })
      )
      .addCase(addJobCommentsAction.pending, (state: JobsTypes) => ({
        ...state,
        jobProgressCommentsFetching: true,
      }))
      .addCase(
        addJobCommentsAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { result = {}, message, status_code, success } = payload;

          if (success === 0) {
            return {
              ...state,
              jobProgressCommentsFetching: false,
              jobProgressCommentsSuccess: success,
              error: message,
              statusCode: status_code,
            };
          }
          return {
            ...state,
            jobProgressCommentsFetching: false,
            jobProgressCommentsSuccess: success,
            recurringResponsiblePeople: result,
            statusCode: status_code,
          };
        }
      )
      .addCase(
        addJobCommentsAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          jobProgressCommentsFetching: false,
          jobProgressCommentsSuccess: 0,
          error: error?.message,
        })
      )
      .addCase(getJobLinksDetailsAction.pending, (state: JobsTypes) => ({
        ...state,
        jobProgressCommentsFetching: true,
      }))
      .addCase(
        getJobLinksDetailsAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { result = {}, message, status_code, success } = payload;

          if (success === 0) {
            return {
              ...state,
              jobProgressCommentsFetching: false,
              jobProgressCommentsSuccess: success,
              error: message,
              statusCode: status_code,
            };
          }
          return {
            ...state,
            jobProgressCommentsFetching: false,
            statusCode: status_code,
            documentsLinkList: result,
          };
        }
      )
      .addCase(
        getJobLinksDetailsAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          error: error?.message,
          jobProgressCommentsFetching: false,
          jobProgressCommentsSuccess: 0,
        })
      )
      .addCase(addJobLinkAction.pending, (state: JobsTypes) => ({
        ...state,
        jobProgressCommentsFetching: true,
      }))
      .addCase(addJobLinkAction.fulfilled, (state: JobsTypes, { payload }) => {
        const { message, status_code, success } = payload;
        if (success === 0) {
          return {
            ...state,
            jobProgressCommentsFetching: false,
            jobProgressCommentsSuccess: success,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          jobProgressCommentsFetching: false,
          jobProgressCommentsSuccess: success,
          statusCode: status_code,
        };
      })
      .addCase(addJobLinkAction.rejected, (state: JobsTypes, { error }) => ({
        ...state,
        jobProgressCommentsFetching: false,
        jobProgressCommentsSuccess: 0,
        error: error?.message,
      }))
      .addCase(getUserMapedClientJobListAction.pending, (state: JobsTypes) => ({
        ...state,
        fetching: true,
      }))
      .addCase(
        getUserMapedClientJobListAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { success, message, result, status_code, total_count } =
            payload;
          if (success === 0) {
            return {
              ...state,
              fetching: false,
              statusCode: status_code,
              error: message,
              // jobProgressCommentsSuccess: success,
            };
          }
          return {
            ...state,
            fetching: false,
            assignedClientsOpenJobsList: result,
            totalAllOpenJobsCount: total_count,
          };
        }
      )
      .addCase(
        getUserMapedClientJobListAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          fetching: false,
          success: 0,
          error: error?.message,
        })
      )
      .addCase(
        getUserMapedClientShareJobListAction.pending,
        (state: JobsTypes) => ({
          ...state,
          fetching: true,
        })
      )
      .addCase(
        getUserMapedClientShareJobListAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { success, message, result, status_code, total_count } =
            payload;
          if (success === 0) {
            return {
              ...state,
              fetching: false,
              statusCode: status_code,
              error: message,
              // jobProgressCommentsSuccess: success,
            };
          }
          return {
            ...state,
            fetching: false,
            assignedClientsShareOpenJobsList: result,
            totalAllOpenShareJobsCount: total_count,
          };
        }
      )
      .addCase(
        getUserMapedClientShareJobListAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          fetching: false,
          success: 0,
          error: error?.message,
        })
      )

      //  unqiue check
      .addCase(checkUniqueJobTypeAction.pending, (state: JobsTypes) => ({
        ...state,
        uniqueCheckFetching: true,
      }))
      .addCase(
        checkUniqueJobTypeAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { success, message, result, is_exist, status_code } = payload;
          if (success === 0) {
            return {
              ...state,
              uniqueCheckFetching: false,
              uniqueCheckStatusCode: status_code,
              uniqueCheckError: message,
              uniqueCheckSuccess: success,
              // isJobType:is_exist,
            };
          }
          return {
            ...state,
            // isJobType:is_exist,
            uniqueCheckFetching: false,
            uniqueCheckMessage: message,
            uniqueCheckStatusCode: status_code,
            uniqueCheckSuccess: success,
            // assignedClientsOpenJobsList: result
          };
        }
      )
      .addCase(
        checkUniqueJobTypeAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          uniqueCheckFetching: false,
          uniqueCheckSuccess: 0,
          error: error?.message,
        })
      )
      .addCase(shareJobsWithTeamMembersAction.pending, (state: JobsTypes) => ({
        ...state,
        fetching: true,
      }))
      .addCase(
        shareJobsWithTeamMembersAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { success, message, status_code } = payload;
          if (success === 0) {
            return {
              ...state,
              fetching: false,
              statusCode: status_code,
              error: message,
              sharedJobSuccess: success,
            };
          }
          return {
            ...state,
            fetching: false,
            message,
            statusCode: status_code,
            sharedJobSuccess: success,
          };
        }
      )
      .addCase(
        shareJobsWithTeamMembersAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          fetching: false,
          sharedJobSuccess: 0,
          error: error?.message,
        })
      )
      //checklist
      .addCase(getJobChecklistDataAction.pending, (state: JobsTypes) => ({
        ...state,
        fetching: true,
      }))
      .addCase(
        getJobChecklistDataAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { success, message, result, status_code, count } = payload;
          if (success === 0) {
            return {
              ...state,
              fetching: false,
              statusCode: status_code,
              error: message,
              success: success,
            };
          }
          return {
            ...state,
            fetching: false,
            jobChecklistData: result,
            jobChecklistCount: count,
          };
        }
      )
      .addCase(
        getJobChecklistDataAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          fetching: false,
          success: 0,
          error: error?.message,
        })
      )
      .addCase(getJobChecklistSingleDataAction.pending, (state: JobsTypes) => ({
        ...state,
        fetching: true,
      }))
      .addCase(
        getJobChecklistSingleDataAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { success, message, result, status_code, total_count } =
            payload;
          if (success === 0) {
            return {
              ...state,
              fetching: false,
              statusCode: status_code,
              error: message,
              success: success,
            };
          }
          return {
            ...state,
            fetching: false,
            jobChecklistSingleData: result,
          };
        }
      )
      .addCase(
        getJobChecklistSingleDataAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          fetching: false,
          success: 0,
          error: error?.message,
        })
      )
      .addCase(addJobChecklistDataAction.pending, (state: JobsTypes) => ({
        ...state,
        fetching: true,
      }))
      .addCase(
        addJobChecklistDataAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { message, status_code, success } = payload;
          if (success === 0) {
            return {
              ...state,
              fetching: false,
              success: success,
              error: message,
              statusCode: status_code,
            };
          }
          return {
            ...state,
            fetching: false,
            success: success,
            statusCode: status_code,
          };
        }
      )
      .addCase(
        addJobChecklistDataAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          fetching: false,
          success: 0,
          error: error?.message,
        })
      )
      .addCase(updateJobChecklistDataAction.pending, (state: JobsTypes) => ({
        ...state,
        fetching: true,
      }))
      .addCase(
        updateJobChecklistDataAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { message, status_code, success } = payload;
          if (success === 0) {
            return {
              ...state,
              fetching: false,
              success: success,
              error: message,
              statusCode: status_code,
            };
          }
          return {
            ...state,
            fetching: false,
            success: success,
            statusCode: status_code,
          };
        }
      )
      .addCase(
        updateJobChecklistDataAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          fetching: false,
          success: 0,
          error: error?.message,
        })
      )
      .addCase(addJobClosureAction.pending, (state: JobsTypes) => ({
        ...state,
        fetching: true,
      }))
      .addCase(
        addJobClosureAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { message, status_code, success } = payload;
          if (success === 0) {
            return {
              ...state,
              fetching: false,
              jobProgressCommentsSuccess: success,
              error: message,
              statusCode: status_code,
            };
          }
          return {
            ...state,
            fetching: false,
            statusCode: status_code,
            jobProgressCommentsSuccess: success,
            message: message,
          };
        }
      )
      .addCase(addJobClosureAction.rejected, (state: JobsTypes, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message,
      }))
      .addCase(
        getAllOpenJobChecklistSingleDataAction.pending,
        (state: JobsTypes) => ({
          ...state,
          fetching: true,
        })
      )
      .addCase(
        getAllOpenJobChecklistSingleDataAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { success, message, result, status_code, total_count } =
            payload;
          if (success === 0) {
            return {
              ...state,
              fetching: false,
              statusCode: status_code,
              error: message,
              success: success,
            };
          }
          return {
            ...state,
            fetching: false,
            allOpenJobChecklistSingleData: result,
          };
        }
      )
      .addCase(
        getAllOpenJobChecklistSingleDataAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          fetching: false,
          success: 0,
          error: error?.message,
        })
      )
      .addCase(getAllOpenJobChecklistAction.pending, (state: JobsTypes) => ({
        ...state,
        fetching: true,
      }))
      .addCase(
        getAllOpenJobChecklistAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { success, message, result, status_code, total_count } =
            payload;
          if (success === 0) {
            return {
              ...state,
              fetching: false,
              statusCode: status_code,
              error: message,
              success: success,
            };
          }
          return {
            ...state,
            fetching: false,
            allOpenJobChecklistData: result,
          };
        }
      )
      .addCase(
        getAllOpenJobChecklistAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          fetching: false,
          success: 0,
          error: error?.message,
        })
      )
      .addCase(updateAllOpenJobChecklistAction.pending, (state: JobsTypes) => ({
        ...state,
      }))
      .addCase(
        updateAllOpenJobChecklistAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { message, status_code, success } = payload;
          if (success === 0) {
            return {
              ...state,
              fetching: false,
              success: success,
              error: message,
              statusCode: status_code,
            };
          }
          return {
            ...state,
            fetching: false,
            success: success,
            statusCode: status_code,
          };
        }
      )
      .addCase(
        updateAllOpenJobChecklistAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          fetching: false,
          success: 0,
          error: error?.message,
        })
      )
      .addCase(lockJobChecklistDataAction.pending, (state: JobsTypes) => ({
        ...state,
      }))
      .addCase(
        lockJobChecklistDataAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { message, status_code, success } = payload;
          if (success === 0) {
            return {
              ...state,
              fetching: false,
              success: success,
              error: message,
              statusCode: status_code,
            };
          }
          return {
            ...state,
            fetching: false,
            success: success,
            statusCode: status_code,
          };
        }
      )
      .addCase(
        lockJobChecklistDataAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          fetching: false,
          success: 0,
          error: error?.message,
        })
      )
      .addCase(
        getCommentDueTomorrowJobListAction.pending,
        (state: JobsTypes) => ({
          ...state,
          fetching: true,
        })
      )
      .addCase(
        getCommentDueTomorrowJobListAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const { success, message, result, status_code, total_count } =
            payload;
          if (success === 0) {
            return {
              ...state,
              fetching: false,
              statusCode: status_code,
              error: message,
              // jobProgressCommentsSuccess: success,
            };
          }
          return {
            ...state,
            fetching: false,
            allCommentDueTomorrowListData: result,
            allCommentDueTomorrowCount: total_count,
          };
        }
      )
      .addCase(
        getCommentDueTomorrowJobListAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          fetching: false,
          success: 0,
          error: error?.message,
        })
      )

      // report
      .addCase(getRecurringJobReportAction.pending, (state: JobsTypes) => ({
        ...state,
        fetching: true,
      }))
      .addCase(
        getRecurringJobReportAction.fulfilled,
        (state: JobsTypes, { payload }) => {
          const {
            result = {},
            message,
            status_code,
            success,
            total_count,
          } = payload;
          if (success === 0) {
            return {
              ...state,
              fetching: false,
              error: message,
              success: 0,
              statusCode: status_code,
            };
          }
          return {
            ...state,
            fetching: false,
            recurringJobReportList: result,
            recurringJobReportTotalCount: total_count,
          };
        }
      )
      .addCase(
        getRecurringJobReportAction.rejected,
        (state: JobsTypes, { error }) => ({
          ...state,
          fetching: false,
          error: error?.message,
          success: 0,
        })
      );
  },
});

export const {
  resetError,
  resetMessage,
  resetSuccess,
  resetSharedJobSuccess,
  resetStatusCode,
  resetFetching,
  resetTotalCount,
  resetRecurringJobsDetailsList,
  resetJobsTypeList,
  resetSingleJobs,
  resetSingleJobsType,
  resetJobsList,
  resetFutureJobsList,
  resetJobsTotalCount,
  resetRecurringResponsiblePeople,
  resetJobProgressCommentsSuccess,
  resetJobProgressCommentsListing,
  resetDocumentsLinkList,
  resetAssignedClientsOpenJobsList,
  resetUniqueCheckMessage,
  resetUniqiueCheckFetching,
  resetUniqueCheckSuccess,
  resetUniqueCheckStatusCode,
  resetTotalRecurringJobsListCount,
  resetAssignedClientsShareOpenJobsList,
  resetJobChecklistData,
  resetJobChecklistSingleData,
  resetJobCheckTotalCount,
  resetAllOpenJobChecklistData,
  resetAllOpenJobChecklistSingleData,
  resetAllOpenJobCheckTotalCount,
  resetAllCommentDueTomorrowListData,
  resetAllCommentDueTomorrowCount,
  resetRecurringJobReportList,
  resetRecurringJobReportTotalCount,
  setSearchStringInAllOpenJobs,
  setJobPriorityInAllOpenJobs,
  setOpenStatusInAllOpenJobs,
  setOpenStatusValueInAllOpenJobs,
  setSortByTargetEndDateInAllOpenJobs,
  setSortByJobTypeInAllOpenJobs,
  setSortByStatusInAllOpenJobs,
  setSortByClientCodeInAllOpenJobs,
  setSortByClientNameInAllOpenJobs,
  setRecentlyInAllOpenJobs,
  setOffsetInAllOpenJobs,
  resetAllOpenJobsFilters,
  setChecklistModalState,
  resetChecklistModalState,
} = jobsSlice.actions;
